import { differenceInCalendarDays, format, isValid } from 'date-fns';

/**
 * Shared expiration-date formatting for the OPC, flow Command Center and
 * Gamma Exposure expiration dropdowns.
 *
 *   today  -> "0DTE (May 20)"
 *   future -> "May 21, 2026 (1DTE)"
 *
 * DTE = Days To Expiration (calendar days from today). Dates are built at
 * local midnight so a date never renders as the wrong day across timezones.
 */

/** Calendar parts of an expiration date. `month` is 1-indexed (January = 1). */
export interface ExpirationDateParts {
  year: number;
  month: number;
  day: number;
}

export type ExpirationDateInput = string | Date | ExpirationDateParts;

/** Normalizes any accepted input shape into `{ year, month, day }`, or null. */
const toParts = (input: ExpirationDateInput): ExpirationDateParts | null => {
  if (typeof input === 'string') {
    // Accepts "YYYY-MM-DD" and the non-zero-padded "YYYY-M-D".
    const nums = input.split('-').map((part) => Number(part));
    if (nums.length !== 3 || nums.some((n) => !Number.isInteger(n))) {
      return null;
    }
    return { year: nums[0], month: nums[1], day: nums[2] };
  }
  if (input instanceof Date) {
    if (!isValid(input)) {
      return null;
    }
    return { year: input.getFullYear(), month: input.getMonth() + 1, day: input.getDate() };
  }
  return input;
};

/** Builds a local-midnight Date from expiration parts (timezone-safe). */
const toLocalDate = (parts: ExpirationDateParts): Date =>
  new Date(parts.year, parts.month - 1, parts.day);

/**
 * Days to expiration: calendar days from today to the expiration date.
 * 0 = expires today, positive = future, negative = already expired.
 * Returns NaN for unparseable input.
 */
export const getDaysToExpiration = (input: ExpirationDateInput, now: Date = new Date()): number => {
  const parts = toParts(input);
  if (!parts) {
    return NaN;
  }
  return differenceInCalendarDays(toLocalDate(parts), now);
};

/**
 * Formats an expiration date as the standard label:
 *   today  -> "0DTE (May 20)"
 *   future -> "May 21, 2026 (1DTE)"
 *
 * Falls back to the original string (or "") when the input cannot be parsed.
 */
export const formatExpirationDate = (
  input: ExpirationDateInput,
  now: Date = new Date(),
): string => {
  const parts = toParts(input);
  if (!parts) {
    return typeof input === 'string' ? input : '';
  }
  const date = toLocalDate(parts);
  const dte = differenceInCalendarDays(date, now);
  if (dte === 0) {
    return `0DTE (${format(date, 'MMM d')})`;
  }
  return `${format(date, 'MMM d, yyyy')} (${dte}DTE)`;
};
